/**
 * @fileoverview Authentication actions and hooks for user authentication, session management, and password reset
 * @module AuthActions
 */

import { signIn, signOut as amplifySignOut } from "aws-amplify/auth";
import apiClient from "../utils/apiClient";
import { getCurrentSession } from "../utils/authSession";
import useSWR, { mutate } from "swr";
import axios from "axios";

/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */

/**
 * Returns the plan for the current user client
 * @function
 * @name usePlan
 * @memberof module:AuthActions
 * @returns {SWRObject} SWR Object containing plan data
 */

export const usePlan = () => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);
  const { data, error } = useSWR("/clients/plan/", fetcher);

  return { data, isLoading: !error && !data, error };
};

/**
 * Authenticates a user with username and password
 * @function
 * @name authUser
 * @memberof module:AuthActions
 * @param {string} username - Email for the user
 * @param {string} password - Password for the user
 * @returns {Promise<Object>} Authentication result from AWS Amplify
 */
export const authUser = async (username, password) => {
  const credentials = { username, password };
  let result;
  try {
    result = await signIn(credentials);
  } catch (err) {
    // v4 replaced an existing session; v6 refuses to sign in over it. The sign-up pages aren't
    // guarded by RedirectIfAuth, so a stale session would keep the new account signed out.
    if (err?.name !== "UserAlreadyAuthenticatedException") throw err;
    await amplifySignOut();
    result = await signIn(credentials);
  }
  // v6 resolves with a pending step (new password, unconfirmed account) instead of a session.
  // No caller handles those steps, so fail with the step name; cognitoMessages translates it.
  if (!result.isSignedIn) {
    throw new Error(result.nextStep?.signInStep ?? "SIGN_IN_NOT_FINISHED");
  }
  // Drop the cached session of whoever was signed in before (e.g. on the sign-up pages)
  await mutate("/session/");
  return result;
};

/**
 * Gets the current AWS Amplify authentication session
 * @function
 * @name getSession
 * @memberof module:AuthActions
 * @returns {Promise<Object>} The current authentication session
 */
export const getSession = () => getCurrentSession();

/**
 * Hook that returns the session for the current user
 * @function
 * @name useSession
 * @memberof module:AuthActions
 * @returns {SWRObject} SWR Object containing session data
 * @example
 * const { data, isLoading, error } = useSession();
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Failed to load</div>;
 * if (data) return <div>Session: {data}</div>;
 */

export const useSession = () => {
  const fetcher = () => getCurrentSession();
  const { data, error } = useSWR("/session/", fetcher);

  return { data, isLoading: !error && !data, error };
};

/**
 * Sends a recovery code to reset password
 * @function
 * @name sendResetPasswordCode
 * @memberof module:AuthActions
 * @param {Object} data - Object containing username (email)
 * @returns {Promise<Object>} API response
 */
export async function sendResetPasswordCode(data) {
  return axios.post(`${import.meta.env.VITE_API_URL}auth/send_code_password/`, data);
}

/**
 * Sends the recovery code and required data to reset password
 * @function
 * @name sendResetPassword
 * @memberof module:AuthActions
 * @param {Object} data - Object containing username, code and password
 * @returns {Promise<Object>} API response
 */
export async function sendResetPassword(data) {
  return axios.post(`${import.meta.env.VITE_API_URL}auth/reset_password/`, data);
}

/**
 * Signs out the current user from AWS Amplify authentication
 * @function
 * @name signOut
 * @memberof module:AuthActions
 * @returns {Promise<void>} Promise that resolves when sign out is complete
 */
export async function signOut() {
  await amplifySignOut();
}
